import {
  Button,
  Input,
  isValidPhoneNumber,
  Label,
  PhoneInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { RotateCcw } from 'lucide-react';
import de from 'react-phone-number-input/locale/de';
import en from 'react-phone-number-input/locale/en';

import { useLocale } from '@/core/hooks/use-locale';
import type { TemplateMemberValueColumn } from '@/core/lib/profile-template-member-values';
import { getSocialUrl, getSocialUsername } from '@/core/lib/socials';
import { profileCellDiffersFromStandard } from '@/core/lib/template-member-value-column-api';
import type { SocialPlatform } from '@/core/types/profile-modules';

type TemplateMemberValueCellProps = {
  memberUserId: string;
  column: TemplateMemberValueColumn;
  cellValue: string;
  defaultDisplay: string;
  canManage: boolean;
  inputPlaceholder: string;
  maxLength?: number;
  resetLabel: string;
  enabledLabel: string;
  layout?: 'inline' | 'stacked';
  onUpdate: (value: string, normalizeWhitespace?: boolean) => void;
};

export function TemplateMemberValueCell({
  memberUserId,
  column,
  cellValue,
  defaultDisplay,
  canManage,
  inputPlaceholder,
  maxLength,
  resetLabel,
  enabledLabel,
  layout = 'inline',
  onUpdate,
}: TemplateMemberValueCellProps) {
  const locale = useLocale();
  const phoneLabels = locale === 'de' ? de : en;
  const stacked = layout === 'stacked';
  const rowClass = cn(
    'flex min-w-0 gap-1',
    stacked ? 'w-full items-stretch' : 'items-center',
  );
  const rowEndClass = cn(rowClass, !stacked && 'justify-end');

  const displayDefaultValue =
    column.kind === 'social'
      ? getSocialUsername(column.platform as SocialPlatform, defaultDisplay)
      : defaultDisplay;

  const displayValue =
    column.kind === 'social'
      ? getSocialUsername(column.platform as SocialPlatform, cellValue)
      : cellValue;

  const usesDefault = !profileCellDiffersFromStandard(
    displayValue,
    displayDefaultValue,
  );
  const resetToDefault = () => onUpdate(defaultDisplay);

  const resetButton = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-8 w-8 shrink-0 text-muted-foreground"
      disabled={!canManage || usesDefault}
      title={resetLabel}
      aria-label={resetLabel}
      onClick={resetToDefault}
    >
      <RotateCcw className="h-3.5 w-3.5" />
    </Button>
  );

  if (column.kind === 'setting' && column.inputType === 'boolean') {
    return (
      <div className={rowEndClass}>
        <div className="flex items-center gap-2">
          <Switch
            id={`member-${memberUserId}-${column.key}`}
            checked={
              usesDefault ? defaultDisplay === 'true' : displayValue === 'true'
            }
            disabled={!canManage}
            onCheckedChange={(checked) => {
              const nextVal = checked ? 'true' : 'false';
              if (defaultDisplay && nextVal === defaultDisplay) {
                onUpdate(defaultDisplay);
                return;
              }
              onUpdate(nextVal);
            }}
          />
          <Label
            htmlFor={`member-${memberUserId}-${column.key}`}
            className="text-xs font-normal text-muted-foreground"
          >
            {enabledLabel}
          </Label>
        </div>
        {resetButton}
      </div>
    );
  }

  if (
    column.kind === 'setting' &&
    column.inputType === 'enum' &&
    column.options
  ) {
    return (
      <div className={rowClass}>
        <Select
          value={
            usesDefault
              ? displayDefaultValue || column.options[0]?.value
              : displayValue || column.options[0]?.value
          }
          disabled={!canManage}
          onValueChange={(value) => {
            if (defaultDisplay && value === defaultDisplay) {
              onUpdate(defaultDisplay);
              return;
            }
            onUpdate(value);
          }}
        >
          <SelectTrigger className="h-8 min-w-0 flex-1 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[110]">
            {column.options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {resetButton}
      </div>
    );
  }

  if (column.kind === 'moduleEntry' && column.inputType === 'phone') {
    return (
      <div className={rowClass}>
        <div className={cn('min-w-0 flex-1', stacked && 'w-full')}>
          <PhoneInput
            defaultCountry="DE"
            labels={phoneLabels}
            value={displayValue || undefined}
            disabled={!canManage}
            placeholder={inputPlaceholder}
            className="h-8 [&_input]:h-8 [&_input]:text-sm"
            aria-invalid={Boolean(
              displayValue && !isValidPhoneNumber(displayValue),
            )}
            onChange={(value) => {
              const nextVal = value ?? '';
              if (
                displayDefaultValue &&
                nextVal.trim() === displayDefaultValue.trim()
              ) {
                onUpdate(defaultDisplay);
                return;
              }
              onUpdate(nextVal);
            }}
          />
        </div>
        {resetButton}
      </div>
    );
  }

  const isEmailInput =
    column.kind === 'moduleEntry' && column.inputType === 'email';
  const isUrlInput =
    (column.kind === 'social' && column.platform === 'custom') ||
    inputPlaceholder.trim().toLowerCase().startsWith('http');
  const isHandleOrUrlInput = column.kind === 'social' || isUrlInput;

  return (
    <div className={rowClass}>
      <Input
        type={isEmailInput ? 'email' : 'text'}
        inputMode={isEmailInput ? 'email' : isUrlInput ? 'url' : undefined}
        autoComplete={isEmailInput ? 'email' : isUrlInput ? 'url' : undefined}
        autoCapitalize={isEmailInput || isHandleOrUrlInput ? 'none' : undefined}
        autoCorrect={isEmailInput || isHandleOrUrlInput ? 'off' : undefined}
        spellCheck={isEmailInput || isHandleOrUrlInput ? false : undefined}
        className={cn('h-8 min-w-0 flex-1 text-sm', stacked && 'w-full')}
        value={displayValue}
        disabled={!canManage}
        maxLength={maxLength}
        placeholder={inputPlaceholder}
        onChange={(e) => {
          const v = e.target.value;
          if (displayDefaultValue && v.trim() === displayDefaultValue) {
            onUpdate(defaultDisplay);
            return;
          }
          if (column.kind === 'social') {
            if (column.platform === 'custom') {
              onUpdate(v);
              return;
            }
            onUpdate(getSocialUrl(column.platform as SocialPlatform, v));
            return;
          }
          onUpdate(v);
        }}
        onBlur={(e) => {
          const trimmed = e.target.value.trim();
          if (trimmed !== e.target.value) {
            if (column.kind === 'social') {
              onUpdate(
                getSocialUrl(column.platform as SocialPlatform, trimmed),
                true,
              );
              return;
            }
            onUpdate(trimmed, true);
          }
        }}
      />
      {resetButton}
    </div>
  );
}
